import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { MultiStepFlowContainer, MultiStepFlowLayout, MultiStepFlowHeader, MultiStepFlowFooter } from "../Components/MultiStepFlow"
import { onboardingFlowSteps, onboardingInitialValues } from "../Data/onboarding-flow"
import api from "../services/api"
import Spinner from "../Components/Spinner"
import ErrorAlert from "../Components/ErrorAlert"
import { useAtom } from "jotai"
import { userAtom } from "../state"
import { useOrganizations } from "../hooks/useOrganizations"
import { sentryService } from "../services/sentry"
import { LOGO } from "../constants/textConstants"

const OnboardingFlow = () => {
  const navigate = useNavigate()
  const [, setUser] = useAtom(userAtom)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [magicLinkEmail, setMagicLinkEmail] = useState("")
  const { organizationOptions } = useOrganizations()

  const steps = onboardingFlowSteps(organizationOptions)

  const handleComplete = async values => {
    setIsSubmitting(true)
    setError(null)
    try {
      const response = await api.registerPasswordless({
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        organization_id: values.organization_id === "none" ? null : values.organization_id,
        marketing_opted_in: values.marketing_opted_in,
      })

      if (response.data) {
        setUser(response.data)
        navigate("/onboarding/tour")
      }
    } catch (err) {
      const emailError = err.response?.data?.errors?.email?.[0]
      const isDuplicateEmail = emailError === "An account with this email already exists."

      if (isDuplicateEmail) {
        try {
          await api.requestMagicLink(values.email)
          setMagicLinkEmail(values.email)
        } catch (magicLinkError) {
          setError({
            title: "Account Already Exists",
            message: "An account with this email already exists. Please go to the login page to sign in.",
          })
          sentryService.captureException(magicLinkError)
          setIsSubmitting(false)
        }
      } else {
        setError({
          title: "Registration Failed",
          message: err.response?.data?.message || "Unable to register. Please check your information and try again.",
        })
        sentryService.captureException(err)
        setIsSubmitting(false)
      }
    }
  }

  if (magicLinkEmail) {
    return (
      <MultiStepFlowLayout
        header={
          <MultiStepFlowHeader
            showBackButton={false}
            logo={LOGO}
            currentStep={0}
            totalSteps={1}
            showProgress={false}
          />
        }
        footer={<MultiStepFlowFooter />}
      >
        <div className="mx-auto min-h-[60vh] max-w-screen-md flex flex-col items-center justify-center text-center">
          <h2 className="mb-4 text-2xl font-bold text-brandSecondary">Check your email</h2>
          <p className="mb-2 text-lg text-gray-600">
            We sent a login link to <strong>{magicLinkEmail}</strong>
          </p>
          <p className="mb-8 text-gray-600">
            An account already exists with this email. Click the link in the email to log in — it expires in 15 minutes.
          </p>
          <button
            onClick={() => setMagicLinkEmail("")}
            className="text-sm text-brand underline"
          >
            Send another link
          </button>
        </div>
      </MultiStepFlowLayout>
    )
  }

  return (
    <>
      {isSubmitting && <Spinner overlay message="Saving..." />}
      {error && (
        <ErrorAlert
          title={error.title}
          message={error.message}
          onDismiss={() => setError(null)}
          className="mb-6"
        />
      )}
      <MultiStepFlowContainer
        steps={steps}
        initialValues={onboardingInitialValues}
        onComplete={handleComplete}
        completeButtonText="Next"
        fieldWidth="max-w-72 mx-auto w-full"
        fieldContainerClassName="flex flex-col items-center"
        totalStepsOverride={8}
        stepProgressModifier={1}
      />
    </>
  )
}

export { OnboardingFlow }
